import React, { useCallback, useRef, useState } from 'react';

import { Helmet } from 'react-helmet';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useExecuteCruiseIqQuery } from '~/hooks/apis/cruises/useCruiseIqQuery';

import { type CruiseIqQueryResult } from '~/services/cruises/CruiseIqService';

import HealthReportPanel from './HealthReportPanel';
import RelationshipExplorer from './RelationshipExplorer';
import { SUGGESTED_QUERIES } from './constants';

const TAB_NL_QUERY = 0;
const TAB_HEALTH_REPORT = 1;
const TAB_RELATIONSHIP_EXPLORER = 2;

function formatColumnHeader(key: string): string {
  let label = key;

  if (label.startsWith('fk')) {
    label = label.slice(2);
  }

  label = label.replace(/_/g, ' ');
  label = label.replace(/([a-z])([A-Z])/g, '$1 $2');

  return label
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function buildColumns(rows: Array<Record<string, unknown>>): GridColDef[] {
  if (rows.length === 0) return [];

  return Object.keys(rows[0]).map((key) => ({
    field: key,
    headerName: formatColumnHeader(key),
    flex: 1,
    minWidth: 150,
    renderCell: (params) => {
      const value = params.value;
      if (value === null || value === undefined) return '-';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    },
  }));
}

function addRowIds(rows: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
  return rows.map((row, index) => ({
    ...row,
    _gridId: row.id ?? index,
  }));
}

function serializeCsvValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function escapeCsvCell(value: string): string {
  if (value.includes('"') || value.includes(',') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function buildCsvContent(rows: Array<Record<string, unknown>>): string {
  if (rows.length === 0) return '';

  const columns = Array.from(
    rows.reduce((keys, row) => {
      Object.keys(row).forEach((key) => {
        if (key !== '_gridId') {
          keys.add(key);
        }
      });
      return keys;
    }, new Set<string>()),
  );

  const header = columns.map((column) => escapeCsvCell(formatColumnHeader(column))).join(',');
  const lines = rows.map((row) => columns.map((column) => escapeCsvCell(serializeCsvValue(row[column]))).join(','));

  return [header, ...lines].join('\n');
}

function buildCsvFilename(): string {
  const now = new Date();
  const parts = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
    String(now.getHours()).padStart(2, '0'),
    String(now.getMinutes()).padStart(2, '0'),
  ];

  return `cruise-iq-results-${parts.join('-')}.csv`;
}

const HISTORY_KEY = 'cruiseiq-history';
const MAX_SUGGESTIONS = 5;

type QueryCount = { query: string; count: number };

function getQueryHistory(): QueryCount[] {
  try {
    const stored = localStorage.getItem(HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function getPopularQueries(): string[] {
  return getQueryHistory()
    .sort((a, b) => b.count - a.count)
    .map((entry) => entry.query);
}

function saveToHistory(queryText: string): void {
  try {
    const history = getQueryHistory();
    const existing = history.find((entry) => entry.query === queryText);
    if (existing) {
      existing.count += 1;
    } else {
      history.push({ query: queryText, count: 1 });
    }
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  } catch {
    // localStorage unavailable
  }
}

function CruiseIqPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<CruiseIqQueryResult | null>(null);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);
  const [popularQueries, setPopularQueries] = useState<string[]>(getPopularQueries);
  const startTimeRef = useRef<number>(0);

  const { mutate, isPending, error, reset: resetMutation } = useExecuteCruiseIqQuery();

  const refreshHistory = useCallback(() => setPopularQueries(getPopularQueries()), []);

  const runQuery = useCallback(
    (queryText: string, options?: { resetResult?: boolean; saveHistory?: boolean }) => {
      if (!queryText.trim()) return;

      if (options?.resetResult) {
        setResult(null);
      }

      startTimeRef.current = Date.now();
      mutate(
        {
          query: queryText.trim(),
        },
        {
          onSuccess: (data) => {
            setElapsedMs(Date.now() - startTimeRef.current);
            setResult(data);

            if (options?.saveHistory) {
              saveToHistory(queryText.trim());
              refreshHistory();
            }
          },
        },
      );
    },
    [mutate, refreshHistory],
  );

  const submitQuery = (queryText: string) => {
    if (!queryText.trim()) return;
    runQuery(queryText, { resetResult: true, saveHistory: true });
  };

  const handleSubmit = () => submitQuery(query);

  const handleChipClick = (suggestedQuery: string) => {
    setQuery(suggestedQuery);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleReset = () => {
    setQuery('');
    setResult(null);
    setElapsedMs(null);
    resetMutation();
  };

  const handleDownloadCsv = useCallback(() => {
    if (!result || result.rows.length === 0) return;

    const csvContent = buildCsvContent(result.rows);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = downloadUrl;
    link.download = buildCsvFilename();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  }, [result]);

  const columns = result ? buildColumns(result.rows) : [];
  const rows = result ? addRowIds(result.rows) : [];
  const hasResults = result !== null;

  return (
    <Box>
      <Helmet>
        <title>Cruise AI Insights</title>
      </Helmet>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
            Cruise AI Insights
          </Typography>
          <Chip
            label="HACKATHON"
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: '0.65rem',
              letterSpacing: '0.08em',
              height: 22,
              bgcolor: 'grey.900',
              color: 'common.white',
            }}
          />
        </Stack>
        {activeTab === TAB_NL_QUERY && hasResults && (
          <Tooltip title="Clear results and start over">
            <IconButton onClick={handleReset} size="small" sx={{ color: 'text.secondary' }}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{
          mb: 4,
          minHeight: 40,
          '& .MuiTab-root': {
            minHeight: 40,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.9rem',
            px: 0,
            mr: 3,
          },
        }}
      >
        <Tab label="Natural Language Query" />
        <Tab label="Health Report" />
        <Tab label="Relationship Explorer" />
      </Tabs>

      {activeTab === 1 && <HealthReportPanel />}
      {activeTab === 2 && <RelationshipExplorer />}
      {activeTab === 0 && (
        <>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Ask a question about cruise data..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isPending}
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: '1rem',
                  },
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={isPending || !query.trim()}
                endIcon={!isPending && <SendIcon fontSize="small" />}
                sx={{
                  minWidth: 110,
                  height: 56,
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  boxShadow: 2,
                }}
              >
                {isPending ? <CircularProgress size={22} color="inherit" /> : 'Ask'}
              </Button>
            </Stack>

            <Stack direction="row" spacing={1} mt={2.5} flexWrap="wrap" useFlexGap>
              {[...popularQueries, ...SUGGESTED_QUERIES.filter((s) => !popularQueries.includes(s))]
                .slice(0, MAX_SUGGESTIONS)
                .map((suggestion) => (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    size="small"
                    variant="outlined"
                    onClick={() => handleChipClick(suggestion)}
                    disabled={isPending}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: '16px',
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  />
                ))}
            </Stack>

            {isPending && <LinearProgress sx={{ mt: 3, borderRadius: 1 }} />}
          </Paper>

          {error && (
            <Box mt={3}>
              <Alert
                severity="error"
                variant="outlined"
                action={
                  <Button color="inherit" size="small" onClick={handleSubmit}>
                    Retry
                  </Button>
                }
              >
                {(error as Error).message || 'Something went wrong'}
              </Alert>
            </Box>
          )}

          {!hasResults && !isPending && !error && (
            <Box mt={8} textAlign="center">
              <AutoAwesomeIcon sx={{ fontSize: 56, color: 'grey.300', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
                Ask anything about your cruise data
              </Typography>
              <Typography variant="body2" color="text.disabled" maxWidth={400} mx="auto">
                Try one of the suggested queries above, or type your own question. Results will appear here.
              </Typography>
            </Box>
          )}

          <Fade in={hasResults} timeout={400}>
            <Box mt={3}>
              {result && (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={2}
                    sx={{ px: 3, py: 2.5 }}
                  >
                    <Stack spacing={0.75}>
                      <Typography variant="h6" fontWeight={700}>
                        Query Results
                      </Typography>
                      <Stack spacing={0.5}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip
                            label={`${result.rowCount} ${result.rowCount === 1 ? 'row' : 'rows'}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 700 }}
                          />
                          {elapsedMs !== null && (
                            <Chip
                              label={`${(elapsedMs / 1000).toFixed(1)}s`}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontFamily: 'monospace',
                                bgcolor: 'grey.100',
                                color: 'text.secondary',
                              }}
                            />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          Displayed results capped at 10,000 rows
                        </Typography>
                      </Stack>
                    </Stack>
                    <Stack spacing={0.75} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                      {result.rowCount > 0 && (
                        <>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadIcon fontSize="small" />}
                            onClick={handleDownloadCsv}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                          >
                            Download CSV
                          </Button>
                          <Typography variant="caption" color="text.secondary">
                            CSV download capped at 10,000 rows
                          </Typography>
                        </>
                      )}
                    </Stack>
                  </Stack>

                  <Box
                    sx={{
                      mx: 3,
                      mb: 2.5,
                      px: 2.5,
                      py: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      lineHeight: 1.6,
                      color: 'text.secondary',
                    }}
                  >
                    {result.explanation}
                  </Box>

                  <Divider />

                  {result.rows.length > 0 ? (
                    <DataGrid
                      rows={rows}
                      columns={columns}
                      getRowId={(row) => row._gridId}
                      loading={isPending}
                      autoHeight
                      disableRowSelectionOnClick
                      disableColumnMenu
                      sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                          bgcolor: 'grey.50',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.08em',
                          color: 'text.secondary',
                        },
                        '& .MuiDataGrid-cell': {
                          fontSize: '0.875rem',
                        },
                        '& .MuiDataGrid-row:hover': {
                          bgcolor: 'grey.50',
                        },
                      }}
                    />
                  ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography color="text.secondary">No results found.</Typography>
                    </Box>
                  )}
                </Paper>
              )}
            </Box>
          </Fade>
        </>
      )}
    </Box>
  );
}

export default CruiseIqPage;
